import { makeAutoObservable } from "mobx";
import { createStoreLogger } from "../../helpers/loggers";

/**
 * Centralized loading state management store.
 * Tracks multiple named loading sources and provides a global loading state.
 */
export class LoadingStore {
  private loadingSources: Set<string> = new Set();
  protected logger = createStoreLogger(this.constructor.name);

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  /**
   * Register a loading source by name
   */
  public setLoading = (sourceName: string, isLoading: boolean) => {
    if (isLoading) {
      this.loadingSources.add(sourceName);
      this.logger.debug(`Loading started: ${sourceName}. Active sources: ${this.loadingSources.size}`);
    } else {
      this.loadingSources.delete(sourceName);
      this.logger.debug(`Loading finished: ${sourceName}. Active sources: ${this.loadingSources.size}`);
    }
  };

  /**
   * Force remove a loading source (useful for cleanup)
   */
  public removeLoadingSource = (sourceName: string) => {
    this.loadingSources.delete(sourceName);
    this.logger.debug(`Loading source removed: ${sourceName}. Active sources: ${this.loadingSources.size}`);
  };

  /**
   * Clear all loading sources
   */
  public clearAllLoading = () => {
    this.loadingSources.clear();
    this.logger.debug("All loading sources cleared");
  };

  /**
   * Check if a specific source is loading
   */
  public isSourceLoading = (sourceName: string): boolean => {
    return this.loadingSources.has(sourceName);
  };

  /**
   * Get all currently loading sources
   */
  public get loadingSourcesList(): string[] {
    return Array.from(this.loadingSources);
  }

  /**
   * Global loading state - true if any source is loading
   */
  public get isLoading(): boolean {
    return this.loadingSources.size > 0;
  }

  /**
   * Get count of active loading sources
   */
  public get loadingCount(): number {
    return this.loadingSources.size;
  }
}

// Create singleton instance
const loadingStore = new LoadingStore();
export default loadingStore;